import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Conseils de Léon qui tournent
const leonTips = [
  "Un devis doit toujours préciser les quantités exactes.",
  "Prévois toujours 10% de marge pour les imprévus.",
  "Vérifie que l'artisan a bien une assurance décennale.",
  "Compare au moins 3 devis avant de te décider.",
  "Les prix TTC doivent être clairement indiqués.",
  "Méfie-toi des devis trop vagues ou incomplets.",
  "Un bon devis détaille les matériaux utilisés.",
  "Demande toujours la date de validité du devis.",
  "Les acomptes ne doivent pas dépasser 30%.",
  "Fais attention aux frais de déplacement cachés."
];

export default function LeonAnimated({ className = "w-24 h-24", showTip = true, tipInterval = 8000 }) {
  const [isBlinking, setIsBlinking] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);

  // Animation de clignement des yeux
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 150);
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(blinkInterval);
  }, []);

  // Rotation des conseils
  useEffect(() => {
    if (!showTip) return;
    const tipTimer = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % leonTips.length);
    }, tipInterval);

    return () => clearInterval(tipTimer);
  }, [showTip, tipInterval]);

  return (
    <div className="flex flex-col items-center">
      {/* Avatar animé */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="relative"
      >
        {/* Animation de "respiration" légère */}
        <motion.div
          animate={{ 
            y: [0, -3, 0],
            rotate: [-1, 1, -1]
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        >
          <svg 
            viewBox="0 0 100 100" 
            className={className}
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background circle with glow */}
            <defs>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <circle cx="50" cy="50" r="48" fill="#18181b" stroke="#D4AF37" strokeWidth="2" filter="url(#glow)"/>
            
            {/* Face - warm skin tone */}
            <ellipse cx="50" cy="55" rx="28" ry="30" fill="#F5D0A9"/>
            
            {/* Hard hat - Gold/Yellow */}
            <ellipse cx="50" cy="28" rx="32" ry="18" fill="#D4AF37"/>
            <rect x="18" y="25" width="64" height="12" rx="2" fill="#D4AF37"/>
            <rect x="22" y="35" width="56" height="4" rx="1" fill="#B38728"/>
            
            {/* Hard hat brim */}
            <ellipse cx="50" cy="38" rx="35" ry="6" fill="#B38728"/>
            
            {/* Hair peeking out - brown */}
            <path d="M 25 42 Q 30 48 35 44" stroke="#5D4037" strokeWidth="3" fill="none" strokeLinecap="round"/>
            <path d="M 75 42 Q 70 48 65 44" stroke="#5D4037" strokeWidth="3" fill="none" strokeLinecap="round"/>
            
            {/* Eyes - animated blink */}
            {isBlinking ? (
              <>
                {/* Closed eyes */}
                <path d="M 30 55 Q 38 58 46 55" stroke="#2D1B00" strokeWidth="3" fill="none" strokeLinecap="round"/>
                <path d="M 54 55 Q 62 58 70 55" stroke="#2D1B00" strokeWidth="3" fill="none" strokeLinecap="round"/>
              </>
            ) : (
              <>
                {/* Open eyes */}
                <ellipse cx="38" cy="55" rx="8" ry="9" fill="white"/>
                <ellipse cx="62" cy="55" rx="8" ry="9" fill="white"/>
                
                {/* Pupils */}
                <circle cx="40" cy="56" r="5" fill="#2D1B00"/>
                <circle cx="64" cy="56" r="5" fill="#2D1B00"/>
                
                {/* Eye shine */}
                <circle cx="42" cy="54" r="2" fill="white"/>
                <circle cx="66" cy="54" r="2" fill="white"/>
              </>
            )}
            
            {/* Eyebrows - friendly */}
            <path d="M 30 46 Q 38 43 46 46" stroke="#5D4037" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
            <path d="M 54 46 Q 62 43 70 46" stroke="#5D4037" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
            
            {/* Nose - small and cute */}
            <ellipse cx="50" cy="65" rx="3" ry="2" fill="#E8B896"/>
            
            {/* Cheeks - rosy */}
            <circle cx="30" cy="65" r="5" fill="#FFB6B0" opacity="0.5"/>
            <circle cx="70" cy="65" r="5" fill="#FFB6B0" opacity="0.5"/>
            
            {/* Smile - big and happy */}
            <path d="M 38 74 Q 50 84 62 74" stroke="#8B4513" strokeWidth="3" fill="none" strokeLinecap="round"/>
            
            {/* Safety vest collar - Orange/Gold */}
            <path d="M 25 88 L 35 82 L 50 85 L 65 82 L 75 88" fill="#FF9800" stroke="#E65100" strokeWidth="1"/>
            <path d="M 30 90 L 40 86 L 50 88 L 60 86 L 70 90" fill="#D4AF37" stroke="#B38728" strokeWidth="1"/>
            
            {/* Reflective stripes on vest */}
            <line x1="35" y1="85" x2="40" y2="90" stroke="#FFEB3B" strokeWidth="2"/>
            <line x1="65" y1="85" x2="60" y2="90" stroke="#FFEB3B" strokeWidth="2"/>
          </svg>
        </motion.div>

        {/* Petit badge animé "en ligne" */}
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-zinc-900 flex items-center justify-center"
        >
          <span className="text-[8px] text-white font-bold">OK</span>
        </motion.div>
      </motion.div>

      {/* Bulle de conseil animée */}
      {showTip && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentTip}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-4 max-w-xs text-center"
          >
            <div className="bg-zinc-800/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-zinc-700/50 relative">
              {/* Triangle pointer */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-zinc-800/80" />
              <p className="text-zinc-300 text-sm italic">
                "{leonTips[currentTip]}"
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

// Export des conseils pour usage externe
export { leonTips };
